import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Upload,
  FileJson,
  X,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Sparkles,
  Workflow,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const TAMANHO_MAXIMO_ARQUIVO = 5 * 1024 * 1024;

const validarPayloadCliente = (payload) => {
  const erros = [];
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return {
      valido: false,
      erros: ["Arquivo não é um JSON objeto válido."],
    };
  }

  const temNodes = Array.isArray(payload.nodes);
  const temEdges = Array.isArray(payload.edges);
  if (!temNodes && !temEdges) {
    erros.push("Não há campos 'nodes' ou 'edges' no JSON.");
  }

  const isPerabit =
    payload._perabitExport === true && payload._kind === "flow";

  const nodesValidos = temNodes
    ? payload.nodes.filter(
        (n) =>
          n &&
          typeof n === "object" &&
          (typeof n.id === "string" || typeof n.id === "number") &&
          typeof n.type === "string"
      ).length
    : 0;
  const edgesValidas = temEdges
    ? payload.edges.filter(
        (e) =>
          e &&
          typeof e === "object" &&
          (typeof e.id === "string" || typeof e.id === "number") &&
          (typeof e.source === "string" || typeof e.source === "number") &&
          (typeof e.target === "string" || typeof e.target === "number")
      ).length
    : 0;

  if (temNodes && nodesValidos !== payload.nodes.length) {
    erros.push(
      `${payload.nodes.length - nodesValidos} nó(s) inválidos (sem id ou type).`
    );
  }
  if (temEdges && edgesValidas !== payload.edges.length) {
    erros.push(
      `${payload.edges.length - edgesValidas} conexão(ões) inválida(s) (sem id/source/target).`
    );
  }

  if (nodesValidos === 0 && edgesValidas > 0) {
    erros.push(
      "Nenhum nó válido encontrado — não é possível importar só conexões."
    );
  }

  return {
    valido: erros.length === 0 && (nodesValidos > 0 || edgesValidas > 0),
    isPerabit,
    schemaVersion: typeof payload._schemaVersion === "number"
      ? payload._schemaVersion
      : 0,
    nomeOriginal:
      typeof payload.name === "string" && payload.name.trim().length > 0
        ? payload.name.trim()
        : "Fluxo importado",
    isActiveOriginal: typeof payload.isActive === "boolean"
      ? payload.isActive
      : false,
    nodesCount: nodesValidos,
    edgesCount: edgesValidas,
    discarded: {
      nodes: temNodes ? payload.nodes.length - nodesValidos : 0,
      edges: temEdges ? payload.edges.length - edgesValidas : 0,
    },
    erros,
    payload,
  };
};

const lerArquivo = (file) =>
  new Promise((resolve, reject) => {
    if (!(file instanceof File)) return reject(new Error("Arquivo inválido."));
    if (file.size > TAMANHO_MAXIMO_ARQUIVO) {
      return reject(
        new Error(
          `Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(
            2
          )} MB). Tamanho máximo permitido: 5 MB.`
        )
      );
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Falha ao ler o arquivo."));
    reader.onload = () => resolve(String(reader.result || ""));
    reader.readAsText(file, "utf-8");
  });

const ImportFlowModal = ({
  open,
  onOpenChange,
  modo = "criar",
  flowAtual,
  onSucesso,
}) => {
  const inputFileRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [nomeArquivo, setNomeArquivo] = useState("");
  const [analise, setAnalise] = useState(null);
  const [nome, setNome] = useState("");
  const [submetendo, setSubmetendo] = useState(false);

  const modoSubstituir = modo === "substituir";

  const resetState = useCallback(() => {
    setDragging(false);
    setNomeArquivo("");
    setAnalise(null);
    setNome("");
    setSubmetendo(false);
    if (inputFileRef.current) inputFileRef.current.value = "";
  }, []);

  const handleOpenChange = (aberto) => {
    if (!aberto) resetState();
    onOpenChange?.(aberto);
  };

  const processarConteudo = async (texto, nomeDoArquivo) => {
    setNomeArquivo(nomeDoArquivo || "");
    setAnalise(null);
    try {
      const parsed = JSON.parse(texto);
      const res = validarPayloadCliente(parsed);
      setAnalise(res);
      if (res.valido) {
        if (modoSubstituir) {
          setNome(flowAtual?.name || res.nomeOriginal);
        } else {
          setNome(res.nomeOriginal);
        }
        if (res.discarded.nodes || res.discarded.edges) {
          toast.warning(
            "Alguns itens inválidos serão ignorados na importação."
          );
        }
      } else {
        toast.error(
          (res.erros && res.erros[0]) || "Arquivo de fluxo inválido."
        );
      }
    } catch (err) {
      setAnalise({
        valido: false,
        erros: [
          err instanceof SyntaxError
            ? `JSON inválido (sintaxe): ${err.message}`
            : err?.message || "Falha ao processar arquivo.",
        ],
      });
      toast.error("Falha ao ler o JSON do arquivo.");
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const txt = await lerArquivo(file);
      await processarConteudo(txt, file.name);
    } catch (err) {
      setNomeArquivo(file.name || "");
      toastError(err);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const file =
      e.dataTransfer?.files && e.dataTransfer.files.length
        ? e.dataTransfer.files[0]
        : null;
    handleFile(file);
  };

  useEffect(() => {
    if (modoSubstituir && flowAtual?.name) setNome(flowAtual.name);
  }, [modoSubstituir, flowAtual, open]);

  const podeConfirmar = useMemo(() => {
    if (!analise || !analise.valido) return false;
    if (nome.trim().length < 2) return false;
    if (submetendo) return false;
    return true;
  }, [analise, nome, submetendo]);

  const handleConfirmar = async () => {
    if (!podeConfirmar || !analise) return;
    setSubmetendo(true);
    try {
      let resposta;
      if (modoSubstituir && flowAtual) {
        resposta = await api.put(`/flows/${flowAtual.id}/import`, {
          payload: analise.payload,
          name: nome.trim(),
        });
      } else {
        resposta = await api.post("/flows/import", {
          payload: analise.payload,
          name: nome.trim(),
        });
      }

      const { flow, info } = resposta.data || {};
      if (!flow) throw new Error("Resposta inválida do servidor.");

      const partes = [];
      if (modoSubstituir) {
        partes.push(`Fluxo "${flow.name}" substituído.`);
      } else {
        partes.push(`Fluxo "${flow.name}" importado com sucesso.`);
      }
      partes.push(
        `${info?.nodes ?? analise.nodesCount} nós · ${
          info?.edges ?? analise.edgesCount
        } conexões.`
      );
      if (info?.isPerabitExport) {
        partes.push(
          `Schema Perabit v${info?.schemaVersion ?? analise?.schemaVersion ?? 0}.`
        );
      }
      toast.success(partes.join(" "));
      if (info?.startedActive) {
        toast.info(
          "O fluxo importado estava ativo — ele foi criado desligado para segurança. Ligue quando revisar."
        );
      }
      handleOpenChange(false);
      onSucesso?.(flow, { modo, info });
    } catch (err) {
      toastError(err);
    } finally {
      setSubmetendo(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            {modoSubstituir
              ? `Importar e substituir · ${flowAtual?.name ?? "fluxo"}`
              : "Importar fluxo"}
          </DialogTitle>
          <DialogDescription>
            {modoSubstituir
              ? "Envie um arquivo JSON exportado do Perabit para sobrescrever este fluxo. O nome, nós e conexões atuais serão substituídos — ativação (isActive) não é alterada por segurança."
              : "Envie um arquivo JSON exportado do Perabit. O fluxo nascerá desligado para segurança; ligue-o após revisar os nós."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => inputFileRef.current?.click()}
            className={`group relative flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-8 text-center transition-all ${
              dragging
                ? "border-primary bg-primary/5"
                : analise?.valido
                  ? "border-emerald-500/60 bg-emerald-500/5"
                  : analise && !analise.valido
                    ? "border-destructive/60 bg-destructive/5"
                    : "border-muted-foreground/25 bg-muted/20 hover:border-primary/50 hover:bg-primary/5"
            }`}
          >
            <input
              ref={inputFileRef}
              type="file"
              accept="application/json,.json,text/json"
              className="hidden"
              onChange={(e) =>
                handleFile(e.target.files && e.target.files[0])
              }
            />
            <div
              className={`flex h-14 w-14 items-center justify-center rounded-2xl ${
                analise?.valido
                  ? "bg-emerald-500/15 text-emerald-600"
                  : analise && !analise.valido
                    ? "bg-destructive/15 text-destructive"
                    : "bg-primary/10 text-primary group-hover:bg-primary/15"
              }`}
            >
              {analise?.valido ? (
                <CheckCircle2 className="h-7 w-7" />
              ) : analise && !analise.valido ? (
                <AlertTriangle className="h-7 w-7" />
              ) : (
                <FileJson className="h-7 w-7" />
              )}
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold">
                {nomeArquivo
                  ? nomeArquivo
                  : "Clique para selecionar ou arraste o .json aqui"}
              </p>
              <p className="text-xs text-muted-foreground">
                Máximo 5 MB · Formato compatível: exportação do Perabit ou JSON
                com {`{ nodes, edges }`}
              </p>
            </div>
            {nomeArquivo && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  resetState();
                }}
                className="absolute right-3 top-3 h-8 px-2"
              >
                <X className="h-4 w-4" />
                Trocar
              </Button>
            )}
          </div>

          {analise && (
            <div
              className={`space-y-3 rounded-xl border p-4 ${
                analise.valido
                  ? "border-emerald-500/40 bg-emerald-500/5"
                  : "border-destructive/40 bg-destructive/5"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                {analise.valido ? (
                  <>
                    <Badge
                      variant="outline"
                      className="border-emerald-500/50 bg-emerald-500/10 text-emerald-700"
                    >
                      <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                      Arquivo válido
                    </Badge>
                    {analise.isPerabit ? (
                      <Badge variant="secondary">
                        <Sparkles className="mr-1 h-3 w-3" />
                        Perabit v{analise.schemaVersion || 0}
                      </Badge>
                    ) : (
                      <Badge variant="outline">Compatibilidade</Badge>
                    )}
                  </>
                ) : (
                  <Badge
                    variant="outline"
                    className="border-destructive/60 bg-destructive/10 text-destructive"
                  >
                    <AlertTriangle className="mr-1 h-3.5 w-3.5" />
                    Inválido para importação
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
                <div className="rounded-lg border bg-background/60 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Nós válidos
                  </div>
                  <div className="mt-1 text-lg font-semibold">
                    {analise.nodesCount}
                  </div>
                </div>
                <div className="rounded-lg border bg-background/60 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Conexões
                  </div>
                  <div className="mt-1 text-lg font-semibold">
                    {analise.edgesCount}
                  </div>
                </div>
                <div className="rounded-lg border bg-background/60 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Itens descartados
                  </div>
                  <div className="mt-1 text-lg font-semibold">
                    {analise.discarded.nodes + analise.discarded.edges}
                  </div>
                </div>
                <div className="rounded-lg border bg-background/60 p-3">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Estava ativo
                  </div>
                  <div className="mt-1 text-lg font-semibold">
                    {analise.isActiveOriginal ? (
                      <span className="text-amber-600">Sim — importado desligado</span>
                    ) : (
                      <span className="text-muted-foreground">Não</span>
                    )}
                  </div>
                </div>
              </div>

              {analise.erros && analise.erros.length > 0 && (
                <ul className="space-y-1 text-xs text-destructive">
                  {analise.erros.slice(0, 4).map((msg, i) => (
                    <li key={i} className="flex gap-2">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{msg}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="flow-import-name">
              Nome do fluxo
              {modoSubstituir && (
                <span className="ml-1 text-xs text-muted-foreground">
                  (renomeia o fluxo atual)
                </span>
              )}
            </Label>
            <Input
              id="flow-import-name"
              value={nome}
              disabled={!analise?.valido || submetendo}
              minLength={2}
              onChange={(e) => setNome(e.target.value)}
              placeholder={
                modoSubstituir
                  ? "Ex.: Fluxo atualizado"
                  : "Ex.: Boas-vindas (cópia do Imobiliária)"
              }
            />
          </div>

          {analise?.valido && !modoSubstituir && (
            <div className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
              <div className="mb-1 flex items-center gap-2 font-medium text-foreground">
                <Workflow className="h-4 w-4" />
                Regras de segurança aplicadas
              </div>
              <ul className="ml-5 list-disc space-y-1">
                <li>O fluxo sempre é criado com status "Desligado".</li>
                <li>
                  Nomes duplicados recebem sufixo <code>(2)</code>,{" "}
                  <code>(3)</code>… automaticamente.
                </li>
                <li>Conexões sem source/target são descartadas.</li>
                <li>
                  Um novo token de webhook é gerado — não substitui nenhum já em
                  uso.
                </li>
              </ul>
            </div>
          )}
          {analise?.valido && modoSubstituir && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-900">
              <div className="mb-1 flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" />
                Atenção · Substituição irreversível
              </div>
              <ul className="ml-5 list-disc space-y-1">
                <li>
                  Os <strong>nós e conexões atuais</strong> deste fluxo serão
                  completamente substituídos.
                </li>
                <li>
                  O <strong>status Ativo</strong> e o <strong>token do webhook</strong> são preservados.
                </li>
                <li>
                  Histórico de execuções deste fluxo não é alterado.
                </li>
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submetendo}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirmar}
            disabled={!podeConfirmar}
          >
            {submetendo ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {modoSubstituir
              ? submetendo
                ? "Substituindo..."
                : "Substituir este fluxo"
              : submetendo
                ? "Importando..."
                : analise?.isPerabit
                  ? "Criar fluxo importado"
                  : "Criar fluxo (modo compatibilidade)"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFlowModal;
